// value-edge.ts — pure helpers shared by the Value Edge Badge (Logic 4)
// and the Market Filter (Logic 5).
//
// Doctrine, per STRIKZA_FreeSuperTips_Intelligence_Logics §5–6:
//   Logic 4: surface STRIKZA's model probability vs the market's
//            implied probability. Three tiers by edge percentage
//            point (pp) gap. Never display a badge when model
//            confidence is too low to anchor the comparison.
//   Logic 5: client-side filter only. Map the existing free-text
//            `market` field to a small enum so leg-row components
//            and the Exchange filter agree on what counts as a
//            "Goals" vs "BTTS" vs "Result" pick.

export type ValueEdgeTier = "value" | "strong" | "sharp";

/** Compute the absolute edge in percentage points (model − market).
 *  Returns null when either probability is null/NaN/out-of-range. */
export function computeEdgePct(
  modelProb: number | null | undefined,
  marketProb: number | null | undefined,
): number | null {
  if (modelProb === null || modelProb === undefined) return null;
  if (marketProb === null || marketProb === undefined) return null;
  if (!Number.isFinite(modelProb) || !Number.isFinite(marketProb)) return null;
  if (modelProb <= 0 || modelProb >= 1) return null;
  if (marketProb <= 0 || marketProb >= 1) return null;
  return +((modelProb - marketProb) * 100).toFixed(2);
}

/** Map an edge (in pp) + model probability to the doc's three-tier
 *  badge. Returns null when:
 *    - edge is null or < 5pp                    (no badge)
 *    - model probability < 0.40                 (low base — doc §5
 *      "never show if model probability is below 40%")
 *
 *  Tier bands (per doc §5):
 *    5–9pp  → value
 *    10–14pp → strong
 *    15+pp  → sharp
 */
export function bandToBadge(
  edgePct: number | null,
  modelProb: number | null | undefined,
): ValueEdgeTier | null {
  if (edgePct === null || !Number.isFinite(edgePct)) return null;
  if (edgePct < 5) return null;
  if (modelProb === null || modelProb === undefined) return null;
  if (!Number.isFinite(modelProb) || modelProb < 0.40) return null;
  if (edgePct >= 15) return "sharp";
  if (edgePct >= 10) return "strong";
  return "value";
}

/** Display label for each tier — used by the badge component header. */
export const VALUE_EDGE_LABEL: Record<ValueEdgeTier, string> = {
  value: "VALUE EDGE",
  strong: "STRONG EDGE",
  sharp: "SHARP EDGE",
};

// ─── Market Filter (Logic 5) ─────────────────────────────────────

/** The five filter modes the user can toggle between. */
export type MarketFilterMode =
  | "all"
  | "result"
  | "goals"
  | "btts"
  | "goalscorer"
  | "correct_score";

/** The primary-market enum a single leg/selection resolves to. The
 *  `correct_score` value is a market mode but not a top-level filter
 *  in the doc spec (correct_score lives inside the goals/result
 *  conversation); we keep it in the enum so leg rows render correctly
 *  but the filter UI only ships 5 user-facing modes. */
export type PrimaryMarket =
  | "result"
  | "goals"
  | "btts"
  | "goalscorer"
  | "correct_score"
  | "other";

const MARKET_PATTERNS: Array<{ re: RegExp; market: PrimaryMarket }> = [
  // Most specific patterns first — match wins immediately.
  { re: /^correct[\s_-]?score/i, market: "correct_score" },
  { re: /^both[\s_-]?teams[\s_-]?to[\s_-]?score|^btts(?:_|\b)/i, market: "btts" },
  // Goals — covers "over_under_2_5", "ou", "Over 2.5 Goals",
  // "Under 2.5 Goals", "Total Goals", "Goal Line", etc.
  { re: /(?:^|_|\b)(over[_\s-]*under|ou|(?:over|under)\s*\d+(?:\.\d+)?\s*goals?|goals?[\s_-]*total|total[\s_-]*goals|goal[\s_-]*line)(?:_|\b)/i, market: "goals" },
  { re: /^(?:anytime|first|last)[\s_-]?(?:goal[\s_-]?)?scorer\b|^goalscorer\b|^bet[\s_-]?to[\s_-]?net\b/i, market: "goalscorer" },
  // M5b-2: gate-live canonical codes that the legacy patterns above
  // miss — team_goals_* is a goals market; double_chance_* and
  // draw_no_bet_* are result markets. Without these the new gate
  // markets fall through to "other" and the UI filter can't see them.
  { re: /^team[\s_-]?goals(?:_|\b)/i, market: "goals" },
  { re: /^double[\s_-]?chance(?:_|\b)/i, market: "result" },
  { re: /^draw[\s_-]?no[\s_-]?bet(?:_|\b)/i, market: "result" },
  { re: /^(?:match[\s_-]?)?(?:result|winner|1x2|moneyline|win[\s_-]?draw[\s_-]?win|wdw|home[\s_-]?win|away[\s_-]?win|draw)\b/i, market: "result" },
  { re: /^match[\s_-]?winner/i, market: "result" },
];

/** Map a free-text market string ("match_winner", "Over 2.5 Goals",
 *  "BTTS Yes", "Anytime Goalscorer", etc) to the primary-market enum.
 *  Returns "other" when nothing matches — the filter then shows the
 *  row only in "All" mode, never under a focused tab. */
export function inferPrimaryMarket(market: string | null | undefined): PrimaryMarket {
  if (!market) return "other";
  const trimmed = market.trim();
  if (trimmed.length === 0) return "other";
  for (const { re, market: m } of MARKET_PATTERNS) {
    if (re.test(trimmed)) return m;
  }
  return "other";
}

/** Filter helper used by both the Exchange row list and any future
 *  surface that wants the same semantics. Keeps "all" as a passthrough
 *  so callers don't have to special-case. Stable sort: relative input
 *  order is preserved within each filtered subset. */
export function filterByMarket<T>(
  rows: readonly T[],
  mode: MarketFilterMode,
  marketOf: (row: T) => string | null | undefined,
): readonly T[] {
  if (mode === "all") return rows;
  return rows.filter((r) => inferPrimaryMarket(marketOf(r)) === mode);
}
